/**
 * P1.4b — is a FROZEN decision snapshot behind the site's evidence?
 *
 * The Executive Summary carries gen-time snapshots (`executiveDecisionRollupAtGen`,
 * `pathToGoSnapshotAtGen`) so the live viewer and the cold-store print / export
 * subtree render one band. Frozen is right — a report says what it said on the
 * day it was generated — but a frozen Proceed used to survive a subsequent red
 * analysis indefinitely with no marker, in the viewer and in every office
 * export, and the viewer's staleness gate was unreachable for every report that
 * carried the snapshot.
 *
 * The honest, deterministic signal both surfaces can share: decision-source
 * siblings generated AFTER this report. It needs no store recompute (so the
 * exporters can evaluate it from a sibling list) and it never blanks the band —
 * it annotates it.
 */
import type { SiteRagReport } from '../../types/siteRag';
import { isAdvisoryReportType, pillarsForReportType } from '../portfolio/pillarRegistry';

/**
 * What a sibling needs to carry. The hold / draft fields are optional so a
 * server-side ref (four fields from the document) and a full browser report
 * both qualify; when present they exclude siblings the decision never reads.
 */
export type DecisionSnapshotSibling =
  Pick<SiteRagReport, 'id' | 'siteId' | 'reportType' | 'createdAt'>
  & Partial<Pick<SiteRagReport, 'generationTier' | 'isDraft' | 'qualityHold' | 'degradedMode' | 'requiresRegeneration'>>;

/**
 * Review M3 (2026-09-05) — a sibling counts only if the decision can read it:
 * its type feeds a decision pillar (not the advisory Connectivity pillar, not
 * a Custom Brief, never another Executive Summary), and it is not a draft, a
 * degraded run, or one flagged for regeneration — the same disqualifiers
 * `reportSatisfiesGo` applies, read from the fields a sibling ref carries.
 */
export function isDecisionSourceSibling(sibling: DecisionSnapshotSibling): boolean {
  const type = sibling.reportType ?? 'feasibility';
  if (type === 'executive-summary') return false;
  if (isAdvisoryReportType(type) || pillarsForReportType(type).length === 0) return false;
  if (sibling.generationTier === 'draft') return false;
  if (sibling.isDraft && sibling.qualityHold !== 'needs-revision') return false;
  if (sibling.degradedMode || sibling.requiresRegeneration) return false;
  return true;
}

export interface DecisionSnapshotStaleness {
  /** Decision-source siblings generated after this report. */
  newerSourceCount: number;
  /** The latest such sibling's `createdAt`, or null. */
  latestNewerAt: string | null;
  /** Report types of the newer siblings, deduped, in first-seen order. */
  newerTypes: string[];
}

const NONE: DecisionSnapshotStaleness = { newerSourceCount: 0, latestNewerAt: null, newerTypes: [] };

export function decisionSnapshotStaleness(
  report: Pick<SiteRagReport, 'id' | 'siteId' | 'createdAt'>,
  siblings: readonly DecisionSnapshotSibling[],
): DecisionSnapshotStaleness {
  const frozenAt = Date.parse(report.createdAt ?? '');
  if (!Number.isFinite(frozenAt)) return NONE;
  const newer = siblings.filter((sibling) => {
    if (sibling.id === report.id) return false;
    if (!isDecisionSourceSibling(sibling)) return false;
    if (report.siteId && sibling.siteId && sibling.siteId !== report.siteId) return false;
    const at = Date.parse(sibling.createdAt ?? '');
    return Number.isFinite(at) && at > frozenAt;
  });
  if (newer.length === 0) return NONE;
  const latest = newer.reduce((best, sibling) =>
    Date.parse(sibling.createdAt) > Date.parse(best.createdAt) ? sibling : best);
  const newerTypes: string[] = [];
  for (const sibling of newer) {
    const type = sibling.reportType ?? 'feasibility';
    if (!newerTypes.includes(type)) newerTypes.push(type);
  }
  return { newerSourceCount: newer.length, latestNewerAt: latest.createdAt, newerTypes };
}

/**
 * One reader-facing sentence, or null when nothing newer exists. Shared by the
 * Decision Guide section, the Path-to-GO section and the DOCX / PPTX emitters
 * so the four cannot describe the same staleness differently.
 */
export function describeDecisionSnapshotStaleness(
  report: Pick<SiteRagReport, 'createdAt'>,
  staleness: DecisionSnapshotStaleness,
): string | null {
  if (staleness.newerSourceCount === 0) return null;
  const frozen = new Date(report.createdAt).toISOString().slice(0, 10);
  const n = staleness.newerSourceCount;
  const types = staleness.newerTypes.join(', ');
  return `Decision basis has moved — this Decision Guide was frozen on ${frozen}; ${n} `
    + `${n === 1 ? 'analysis has' : 'analyses have'} been generated since (${types}). `
    + 'Regenerate the Executive Summary for a current call.';
}
